// ─────────────────────────────────────────────────────────────────
// Glass-i — Company Defaults Master helpers (Stage 6CM.1).
//
// Pure helpers: form hydration, payload build, and field validation.
// There is NO billing/selling/price UOM list here — Price UOM + Input Unit
// options come from the dropdown endpoint (UOM Master). Only internal
// settings enums (chargeable-mode help text) live here as labels.
// ─────────────────────────────────────────────────────────────────

export const DEFAULT_COMPANY_DEFAULTS = Object.freeze({
  defaultWastageMode:              'ROUND_OFF',
  defaultWastageWidthMm:           0,
  defaultWastageHeightMm:          0,
  defaultChargeableMode:           'AUTO_FROM_WASTAGE',
  defaultEditChargeable:           false,
  defaultInputUnitCode:            undefined,
  // STRICT Price-UOM rule: SQM is the default selling UOM and the area mode
  // follows it (SQM ⇒ SQM, SQF ⇒ SQFT). They can never diverge.
  defaultPriceUomCode:             'SQM',
  defaultCompanyStateCode:         undefined,
  defaultAreaMode:                 'SQM',
  autoApplyOtherCharges:           true,
  allowRuntimeOtherChargeOverride: true,
  defaultTaxSource:                'ITEM_MASTER',
  defaultRoundOffEnabled:          true,
  defaultRoundOffMode:             'NEAREST',
  applyToNewProforma:              true,
  applyToExistingDrafts:           false,
  // Edgework RFT/RMT per-side length basis (CHARGE = post-wastage, ACTUAL =
  // entered). CHARGE matches every other billed quantity.
  edgeworkDimensionBasis:          'CHARGE',
  // Proforma approval level (Stage APPROVAL-2). NONE = one-click confirm.
  proformaApprovalLevel:           'NONE'
});

// Two offered choices: NONE (confirm → Work-Order ready) and L1_L2 (Accounts
// review + optional Management Approval). Legacy 'L1' behaves identically to
// NONE after the drawer-as-L1 change, so it folds into NONE here — the backend
// still accepts a stored 'L1', but the UI never sets it again.
const APPROVAL_LEVELS = ['NONE', 'L1_L2'];
const normApprovalLevel = (v) => {
  const u = String(v || '').toUpperCase();
  return APPROVAL_LEVELS.includes(u) ? u : 'NONE';
};

export const EDGEWORK_BASIS_HELP = Object.freeze({
  CHARGE: 'CHARGE: edge lengths use the charge (post-wastage) size — same basis as every other billed quantity.',
  ACTUAL: 'ACTUAL: edge lengths use the actual entered size.'
});
const EDGEWORK_BASES = ['CHARGE', 'ACTUAL'];
const normEdgeworkBasis = (v) => {
  const u = String(v || '').toUpperCase();
  return EDGEWORK_BASES.includes(u) ? u : 'CHARGE';
};

// Stage SALES-PROFORMA-APPROVAL-2 — approval-level help copy.
export const APPROVAL_LEVEL_HELP = Object.freeze({
  NONE:  'None: confirming a proforma from the list drawer makes it Work-Order ready in one click. No Accounts review. Best for small teams.',
  L1_L2: 'Accounts review: after you confirm, Accounts reviews payment/credit before Work-Order release — with an optional Management Approval (the owner) to release on credit.'
});

export const CHARGEABLE_MODE_HELP = Object.freeze({
  AUTO_FROM_WASTAGE:   'AUTO_FROM_WASTAGE: charge size follows the wastage calculation.',
  MANUAL_EDIT_ALLOWED: 'MANUAL_EDIT_ALLOWED: user can edit charge W/H.',
  LOCKED_TO_ACTUAL:    'LOCKED_TO_ACTUAL: charge size stays the same as actual size.',
  LOCKED_TO_WASTAGE:   'LOCKED_TO_WASTAGE: charge size follows wastage and cannot be edited.'
});

const num = (v) => {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const bool = (v, fb) => (v === true || v === false ? v : fb);

// STRICT Price-UOM rule: the area mode is derived from the selling Price UOM —
// SQM ⇒ 'SQM', anything else ⇒ 'SQFT'. They can never diverge.
export function areaModeFromUom(priceUomCode) {
  const u = String(priceUomCode || '').toUpperCase();
  return (u === 'SQM' || u === 'SQMT') ? 'SQM' : 'SQFT';
}

// Merge an API DTO onto the safe defaults for the form.
export function hydrateCompanyDefaults(dto) {
  const s = dto && typeof dto === 'object' ? dto : {};
  return {
    defaultWastageMode:              s.defaultWastageMode || DEFAULT_COMPANY_DEFAULTS.defaultWastageMode,
    defaultWastageWidthMm:           s.defaultWastageWidthMm  != null ? s.defaultWastageWidthMm  : 0,
    defaultWastageHeightMm:          s.defaultWastageHeightMm != null ? s.defaultWastageHeightMm : 0,
    defaultChargeableMode:           s.defaultChargeableMode || DEFAULT_COMPANY_DEFAULTS.defaultChargeableMode,
    defaultEditChargeable:           s.defaultEditChargeable === true,
    defaultInputUnitCode:            s.defaultInputUnitCode || undefined,
    // STRICT: Price UOM is mandatory — default to SQM; Area Mode follows it.
    defaultPriceUomCode:             s.defaultPriceUomCode || DEFAULT_COMPANY_DEFAULTS.defaultPriceUomCode,
    defaultCompanyStateCode:         s.defaultCompanyStateCode || undefined,
    defaultAreaMode:                 areaModeFromUom(s.defaultPriceUomCode || DEFAULT_COMPANY_DEFAULTS.defaultPriceUomCode),
    autoApplyOtherCharges:           bool(s.autoApplyOtherCharges, true),
    allowRuntimeOtherChargeOverride: bool(s.allowRuntimeOtherChargeOverride, true),
    defaultTaxSource:                s.defaultTaxSource || DEFAULT_COMPANY_DEFAULTS.defaultTaxSource,
    defaultRoundOffEnabled:          bool(s.defaultRoundOffEnabled, true),
    defaultRoundOffMode:             s.defaultRoundOffMode || DEFAULT_COMPANY_DEFAULTS.defaultRoundOffMode,
    applyToNewProforma:              bool(s.applyToNewProforma, true),
    applyToExistingDrafts:           bool(s.applyToExistingDrafts, false),
    edgeworkDimensionBasis:          normEdgeworkBasis(s.edgeworkDimensionBasis),
    proformaApprovalLevel:           normApprovalLevel(s.proformaApprovalLevel)
  };
}

// Build the PUT payload from form values.
export function buildCompanyDefaultsPayload(values) {
  const v = values || {};
  return {
    defaultWastageMode:              v.defaultWastageMode || 'ROUND_OFF',
    defaultWastageWidthMm:           num(v.defaultWastageWidthMm),
    defaultWastageHeightMm:          num(v.defaultWastageHeightMm),
    defaultChargeableMode:           v.defaultChargeableMode || 'AUTO_FROM_WASTAGE',
    defaultEditChargeable:           v.defaultEditChargeable === true,
    defaultInputUnitCode:            v.defaultInputUnitCode || null,
    // STRICT: Price UOM defaults to SQM and the area mode is derived from it.
    defaultPriceUomCode:             v.defaultPriceUomCode || 'SQM',
    defaultCompanyStateCode:         v.defaultCompanyStateCode || null,
    defaultAreaMode:                 areaModeFromUom(v.defaultPriceUomCode || 'SQM'),
    autoApplyOtherCharges:           bool(v.autoApplyOtherCharges, true),
    allowRuntimeOtherChargeOverride: bool(v.allowRuntimeOtherChargeOverride, true),
    defaultTaxSource:                v.defaultTaxSource || 'ITEM_MASTER',
    defaultRoundOffEnabled:          bool(v.defaultRoundOffEnabled, true),
    defaultRoundOffMode:             v.defaultRoundOffMode || 'NEAREST',
    applyToNewProforma:              bool(v.applyToNewProforma, true),
    applyToExistingDrafts:           bool(v.applyToExistingDrafts, false),
    edgeworkDimensionBasis:          normEdgeworkBasis(v.edgeworkDimensionBasis),
    proformaApprovalLevel:           normApprovalLevel(v.proformaApprovalLevel)
  };
}

// Field-level validation; returns { ok, errors: [{ field, message }] }.
export function validateCompanyDefaults(values) {
  const v = values || {};
  const errors = [];
  if (num(v.defaultWastageWidthMm) < 0) {
    errors.push({ field: 'defaultWastageWidthMm', message: 'Wastage width cannot be negative.' });
  }
  if (num(v.defaultWastageHeightMm) < 0) {
    errors.push({ field: 'defaultWastageHeightMm', message: 'Wastage height cannot be negative.' });
  }
  return { ok: errors.length === 0, errors };
}
